import React, { memo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import FormField from './FormField';
import Button from './Button';
import Notification from './Notification';

import isArrayWithLength from '../utils/isArrayWithLength';

const FormWrapper = styled.form`
  width: 100%;
`;

const FieldSet = styled.fieldset`
  border: none;
  padding: 0;
`;

const Legend = styled.legend`
  font-size: ${props => props.theme.fontSizes.large};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.black};
  margin-bottom: ${props => props.theme.spacing.midLarge};
`;

function Form({
  fields,
  initialValues,
  onSubmit,
  validationSchema,
  dataTransformer,
  legend,
  submitButtonText,
  loading,
  fullWidthButton,
  error,
  errorMessage,
  success,
  successMessage,
  renderAfterFields,
  utm,
  ...props
}) {
  const formatAndSubmit = values => {
    if (!onSubmit) return;

    if (dataTransformer) {
      onSubmit(dataTransformer(values));
      return;
    }

    onSubmit({ variables: {...values, utm: JSON.stringify(utm)} });
  };

  return (
    <Formik
      enableReinitialize
      validateOnChange={false}
      initialValues={initialValues}
      onSubmit={formatAndSubmit}
      validationSchema={validationSchema && validationSchema}
      {...props}
      render={({ handleSubmit, values, errors }) => {
        return (
          <FormWrapper onSubmit={handleSubmit} noValidate>
            <FieldSet {...props}>
              {legend && <Legend>{legend}</Legend>}

              {isArrayWithLength(fields) &&
                fields.map(field => (
                  <FormField
                    key={field.id}
                    values={values}
                    errors={errors}
                    {...props}
                    {...field}
                  />
                ))}
            </FieldSet>

            {error && <Notification type="red">{errorMessage}</Notification>}

            {success && (
              <Notification type="green">{successMessage}</Notification>
            )}

            {renderAfterFields && renderAfterFields()}

            <Button type="submit" loading={loading} fullWidth={fullWidthButton}>
              {submitButtonText}
            </Button>
          </FormWrapper>
        );
      }}
    />
  );
}

Form.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  initialValues: PropTypes.shape({}),
  onSubmit: PropTypes.func,
  validationSchema: PropTypes.shape({}),
  dataTransformer: PropTypes.func,
  legend: PropTypes.string,
  submitButtonText: PropTypes.string,
  loading: PropTypes.bool,
  fullWidthButton: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  success: PropTypes.bool,
  successMessage: PropTypes.string,
  utm: PropTypes.string,
  renderAfterFields: PropTypes.func,
};

Form.defaultProps = {
  onSubmit: undefined,
  initialValues: {},
  validationSchema: null,
  dataTransformer: undefined,
  legend: null,
  fields: null,
  submitButtonText: 'submit',
  loading: false,
  fullWidthButton: false,
  error: false,
  errorMessage: 'There has been a problem making your request',
  utm: {},
  success: false,
  successMessage: 'Updated successfully',
  renderAfterFields: undefined,
};

export default memo(Form);
